import GenericCrudServiceV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/GenericCrudServiceV2";
import Project from "../models/Project";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";
import HttpClientV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClientV2";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import GenericResponse from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/GenericResponse";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import { ProjectTimelineEntry } from "../types/ProjectTimelineTypes";

class ProjectService extends GenericCrudServiceV2<Project> {
  readonly path: string;

  constructor(path: string) {
    super(path);
    this.path = path;
  }

  findProjectNames(): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/projects-names`);
  }

  findProjectNamesByInternal(internal: boolean): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/projects-names/${internal}`);
  }

  findAllProjectsNames(onlyExternal: boolean, assigned: boolean): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/projects-names-by?onlyExternal=${onlyExternal}&assigned=${assigned}`);
  }

  /**
   * Add a Manager to a Project
   * @param projectExternalId
   * @param userExternalId
   * @throws ApiError from getContentOrThrowError or unwrap; the message is `error.message`
   * @returns `Promise<ApiResponseV2>`
   *
   * @example
   * ```ts
   * ProjectService.addManagerToProject(projectExternalId, userExternalId)
   *   .then(() => console.log('Manager added'))
   *   .catch(e => console.log(e.message));
   * ```
   */
  addManagerToProject(projectExternalId: string, userExternalId: string): Promise<ApiResponseV2<GenericResponse>> {
    return HttpClientV2.post(`${this.basePath}/manager/${projectExternalId}/${userExternalId}`, {});
  }

  /**
   * Remove a Manager from a Project
   * @param projectExternalId
   * @param userExternalId
   * @throws ApiError from getContentOrThrowError or unwrap; the message is `error.message`
   * @returns `Promise<ApiResponseV2>`
   *
   * @example
   * ```ts
   * ProjectService.removeManagerFromProject(projectExternalId, userExternalId)
   *   .then(() => console.log('Manager removed'))
   *   .catch(e => console.log(e.message));
   * ```
   */
  removeManagerFromProject(projectExternalId: string, userExternalId: string): Promise<ApiResponseV2<GenericResponse>> {
    return HttpClientV2.delete(`${this.basePath}/manager/${projectExternalId}/${userExternalId}`);
  }

  /**
   * Check if User is Manager from Project
   * @param projectExternalId
   * @param userExternalId
   * @throws ApiError from getContentOrThrowError or unwrap when the user is not a manager
   * @returns `Promise<ApiResponseV2>`
   *
   * @example
   * ```ts
   * ProjectService.isUserManagerFromProject(projectExternalId, userExternalId)
   *   .then(() => console.log('User is Manager'))
   *   .catch(e => console.log('User is not Manager'));
   * ```
   */
  isUserManagerFromProject(projectExternalId: string, userExternalId: string): Promise<ApiResponseV2<GenericResponse>> {
    return HttpClientV2.get(`${this.basePath}/manager/check/${projectExternalId}/${userExternalId}`);
  }

  /**
   * Check if User is Manager from Project
   * @param projectExternalId
   * @param userCoreExternalId
   * @throws ApiError from getContentOrThrowError or unwrap when the user is not a manager
   * @returns `Promise<ApiResponseV2>`
   *
   * @example
   * ```ts
   * ProjectService.isUserCoreManagerFromProject(projectExternalId, userCoreExternalId)
   *   .then(() => console.log('User is Manager'))
   *   .catch(e => console.log('User is not Manager'));
   * ```
   */
  isUserCoreManagerFromProject(projectExternalId: string, userCoreExternalId: string): Promise<ApiResponseV2<GenericResponse>> {
    return HttpClientV2.get(`${this.basePath}/manager/check-core/${projectExternalId}/${userCoreExternalId}`);
  }

  /**
   * Check if User is Manager from Project using the assignationExternalId
   * @param assignationExternalId
   * @param userCoreExternalId
   * @throws ApiError from getContentOrThrowError or unwrap when the user is not a manager
   * @returns `Promise<ApiResponseV2>`
   *
   * @example
   * ```ts
   * ProjectService.isUserCoreManagerFromProjectByAssignation(assignationExternalId, userCoreExternalId)
   *   .then(() => console.log('User is Manager'))
   *   .catch(e => console.log('User is not Manager'));
   * ```
   */
  isUserCoreManagerFromProjectByAssignation(
    assignationExternalId: string,
    userCoreExternalId: string,
  ): Promise<ApiResponseV2<GenericResponse>> {
    return HttpClientV2.get(`${this.basePath}/manager/check-core-by-assignation/${assignationExternalId}/${userCoreExternalId}`);
  }

  /**
   * Get a list of Managers from a Project
   * @param projectExternalId
   * @throws ApiError from getContentOrThrowError or unwrap; the message is `error.message`
   * @returns `EnsolversOpsUser` array from `Promise<ApiResponseV2>`
   *
   * @example
   * ```ts
   * ProjectService.getManagerListFromProject(projectExternalId, userExternalId)
   *   .then((response) => console.log(response.getContentOrThrowError()))
   *   .catch(e => console.log(e.message));
   * ```
   */
  getManagerListFromProject(projectExternalId: string): Promise<ApiResponseV2<EnsolversOpsUser[]>> {
    return HttpClientV2.get(`${this.basePath}/manager/get/${projectExternalId}`);
  }

  getProjectsFromManager(): Promise<ApiResponseV2<Project[]>> {
    return HttpClientV2.get(`${this.basePath}/manager/get-projects`);
  }

  findProjectsNamesByUserManagerExternalId(): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/manager/projects-names`);
  }

  getAllProjectsByManagerExternalId(
    page: number,
    pageSize: number,
    context: { [key: string]: string | number } = {},
    archived: boolean,
    filter: string,
  ): Promise<ApiResponseV2<PagedResult<Project>>> {
    return HttpClientV2.get(
      `${this.getBasePath(
        context,
      )}/manager/projects-by-user-id?page=${page}&pageSize=${pageSize}&archived=${archived}&projectType=${filter}`,
    );
  }

  findAll(page: number, pageSize: number, archived: boolean, filter: string): Promise<ApiResponseV2<PagedResult<Project>>> {
    return HttpClientV2.get(`${this.basePath}/all/filtered?page=${page}&pageSize=${pageSize}&archived=${archived}&projectType=${filter}`);
  }

  getTimeline(externalId: string): Promise<ApiResponseV2<ProjectTimelineEntry[]>> {
    const searchParams = new URLSearchParams();
    searchParams.set("externalId", externalId);
    return HttpClientV2.get(`${this.basePath}/timeline?${searchParams.toString()}`);
  }
}

export default new ProjectService("/ensolvers-ops/projects");
